/**
 * First-party admin-usage beacon (owner-only analytics, hidden
 * /admin/admin-logs2). Reports page views, labeled clicks, and per-minute
 * visibility heartbeats to POST /api/v1/adminUsage/events.
 *
 * Design rules:
 *  - FAIL-SILENT, always: a dead endpoint must never affect the admin. Every
 *    path is try/caught; the queue is capped; errors drop events.
 *  - Never reads input VALUES — click labels come from data-tour ids,
 *    aria-labels, or trimmed visible text only.
 *  - Identity is stamped server-side from the session cookie; the client only
 *    sends type/page/label/vw/sessionId/ts (`vw` = viewport width on page
 *    views and heartbeats, INT-0042, so device is answerable per organizer).
 *  - Heartbeats fire only while the tab is visible, so "minutes" measures
 *    real attention, not backgrounded tabs.
 */

const ENDPOINT = `${process.env.API_URL}/api/v1/adminUsage/events`;
const FLUSH_MS = 15_000;
const HEARTBEAT_MS = 60_000;
const MAX_QUEUE = 100;
const SESSION_KEY = 'staty_usage_session';

interface UsageEvent {
  type: 'page_view' | 'click' | 'heartbeat';
  page: string;
  label?: string;
  referrerPage?: string;
  /** Viewport width in px; page_view + heartbeat only. */
  vw?: number;
  sessionId: string;
  ts: number;
}

let started = false;
let queue: UsageEvent[] = [];
let lastPage: string | null = null;
// Set on a 401 flush response: the session is gone (expired JWT, logout in
// another tab). Stop collecting/sending until the next full page load —
// re-auth always reloads the app, which resets this. Without it a stale
// dashboard tab beacons a 401 every flush interval indefinitely.
let sessionDead = false;

function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

function viewportWidth(): number | undefined {
  try {
    const w = Math.round(window.innerWidth);
    return Number.isFinite(w) && w > 0 ? w : undefined;
  } catch {
    return undefined;
  }
}

function push(ev: Omit<UsageEvent, 'sessionId' | 'ts'>): void {
  try {
    if (sessionDead) return;
    if (queue.length >= MAX_QUEUE) return;
    queue.push({ ...ev, sessionId: sessionId(), ts: Date.now() });
  } catch {
    /* fail-silent */
  }
}

function flush(useBeacon = false): void {
  try {
    if (sessionDead || !queue.length) return;
    const events = queue;
    queue = [];
    const payload = JSON.stringify({ events });
    if (useBeacon && navigator.sendBeacon) {
      // sendBeacon can't set headers; Blob carries the content type. Cookies
      // (the atoken session) ride along same-origin/credentialed by default.
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    })
      .then((res) => {
        // Session gone → go quiet for the rest of this page's lifetime.
        // (sendBeacon flushes can't observe responses; the 15s fetch flush
        // reaches here soon enough to stop the loop.)
        if (res.status === 401) {
          sessionDead = true;
          queue = [];
        }
      })
      .catch(() => {});
  } catch {
    /* fail-silent */
  }
}

function clickLabel(target: EventTarget | null): string | null {
  try {
    const el = (target as Element | null)?.closest?.('button, a, [role="button"]');
    if (!el) return null;
    const tour = el.getAttribute('data-tour');
    if (tour) return tour;
    const aria = el.getAttribute('aria-label');
    if (aria) return aria.slice(0, 40);
    const text = (el.textContent || '').trim().replace(/\s+/g, ' ');
    return text ? text.slice(0, 40) : null;
  } catch {
    return null;
  }
}

/** Record a route change. Called by the layout on react-router location changes. */
export function trackPageView(pathname: string): void {
  try {
    if (!started || pathname === lastPage) return;
    push({
      type: 'page_view',
      page: pathname,
      referrerPage: lastPage || undefined,
      vw: viewportWidth(),
    });
    lastPage = pathname;
  } catch {
    /* fail-silent */
  }
}

/** Idempotent global start — safe to call from multiple layouts. */
export function startUsageTracker(): void {
  try {
    if (started || typeof window === 'undefined') return;
    started = true;

    document.addEventListener(
      'click',
      (e) => {
        const label = clickLabel(e.target);
        if (label) push({ type: 'click', page: window.location.pathname, label });
      },
      { capture: true, passive: true }
    );

    setInterval(() => {
      if (document.visibilityState === 'visible') {
        push({ type: 'heartbeat', page: window.location.pathname, vw: viewportWidth() });
      }
    }, HEARTBEAT_MS);

    setInterval(() => flush(false), FLUSH_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush(true);
    });
    window.addEventListener('pagehide', () => flush(true));
  } catch {
    /* fail-silent */
  }
}
